import { IReportObs, ObservationStatus } from "../interfaces/markets"

const validStatuses = [ObservationStatus.VALID, ObservationStatus.APPROVED]

export const filterByStatus = (data: IReportObs[], statusId: number): IReportObs[] => {
  return data.filter(item =>
    statusId === ObservationStatus.VALID
      ? validStatuses.includes(item.observationStatusId)
      : item.observationStatusId === statusId,
  )
}

export const countByStatus = (data: IReportObs[], statusId: number): number => {
  return filterByStatus(data, statusId)?.length
}

export const getInvalidKeyClassName = (row: IReportObs, field: string): string => {
  if (row[field] === null) {
    return "bg-red-100 text-red-800 font-bold"
  }
  return ""
}

export const getOutlierClassName = (row: IReportObs): string => {
  if (row.isOutlier) {
    return "bg-yellow-100 text-yellow-800 font-bold"
  }
  return ""
}

export const getOutlierPercentageClassName = (value: number | undefined, threshold: number): string => {
  if (value === undefined) {
    return ""
  }
  if (Math.abs(value) >= threshold) {
    return "bg-yellow-100 text-yellow-800 font-bold"
  }
  return ""
}

export const isItemUpdated = (itemId: number, updatedItems?: IReportObs[]): boolean => {
  return updatedItems?.some(item => item.commodityPriceObservationId === itemId) ?? false
}

const median = (sortedArr: number[]): number => {
  if (!sortedArr?.length) return 0
  const n = sortedArr.length
  const mid = Math.floor(n / 2)

  if (n % 2 === 1) {
    return sortedArr[mid]
  }

  return (sortedArr[mid - 1] + sortedArr[mid]) / 2
}

const quartilesQ1Q3 = (values: number[]): { q1: number | null; q3: number | null } => {
  const data = values
    .filter(item => item !== null && item !== 0)
    .map(item => Number(item))
    .filter(item => Number.isFinite(item))

  if (data?.length < 2) {
    return { q1: null, q3: null }
  }

  data?.sort((a, b) => a - b)

  const n = data?.length
  const mid = Math.floor(n / 2)

  let lowerHalf: number[]
  let upperHalf: number[]

  if (n % 2 === 0) {
    lowerHalf = data?.slice(0, mid)
    upperHalf = data?.slice(mid)
  } else {
    lowerHalf = data?.slice(0, mid)
    upperHalf = data?.slice(mid + 1)
  }

  const q1 = median(lowerHalf)
  const q3 = median(upperHalf)

  return { q1, q3 }
}

const getGroupKey = (obs: IReportObs): string =>
  `${obs.commodityId}_${obs.priceTypeId}_${obs.commodityUnitId}_${obs.currencyId}`

export const detectOutliers = (data: IReportObs[]): IReportObs[] => {
  const groups: { [key: string]: IReportObs[] } = {}

  data.forEach(obs => {
    if (
      obs.commodityId === null ||
      obs.priceTypeId === null ||
      obs.commodityUnitId === null ||
      obs.currencyId === null
    ) {
      return
    }

    const key = getGroupKey(obs)
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(obs)
  })

  const results: IReportObs[] = []

  Object.keys(groups).forEach(groupKey => {
    const observations = groups[groupKey]
    const priceValues = observations.map(obs => obs.commodityPriceValue)

    const { q1, q3 } = quartilesQ1Q3(priceValues)

    if (q1 === null || q3 === null) {
      observations.forEach(obs => {
        results.push({
          ...obs,
          isOutlier: false,
        })
      })
      return
    }

    const iqr = q3 - q1
    const lowerBound = Math.max(0, q1 - 1.5 * iqr)
    const upperBound = q3 + 1.5 * iqr

    observations.forEach(obs => {
      const isOutlier = obs.commodityPriceValue < lowerBound || obs.commodityPriceValue > upperBound

      results.push({
        ...obs,
        isOutlier,
        lowerBound,
        upperBound,
        q1,
        q3,
        iqr,
      })
    })
  })

  return results
}

export const getFetchStatus = (fetchingResponseCodeId: number): string => {
  switch (fetchingResponseCodeId) {
    case 1:
      return "Success"
    case 2:
      return "Error"
    case 3:
      return "Warning"
    default:
      return "N/A"
  }
}
